import { FormEvent, useState } from "react";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Head from "next/head";
import CommonLogo from "@/components/authentication/Others/common/CommonLogo";
import JenisSelect, { JenisOption } from "@/components/OSM/JenisSelect";
import SifatSelect, { SifatOption } from "@/components/OSM/SifatSelect";
import PerihalSelect, { PerihalOption } from "@/components/OSM/PerihalSelect";
import PejabatMultiSelect, { PejabatOption } from "@/components/OSM/PejabatMultiSelect";
import TanggalPicker from "@/components/OSM/TanggalPicker";
import { SuratMasukPayload } from "Types/OSM/SuratMasuk";
import { SuratPenerima } from "Types/OSM/SuratPenerima";
import { uploadLampiranFiles } from "helper/UploadLampiran";

// Generate random captcha
const generateCaptcha = () => {
  const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
  let captcha = "";
  for (let i = 0; i < 4; i++) {
    captcha += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return captcha;
};

const KotakMasukPage = () => {
  // State untuk form
  const [selectedJenis, setSelectedJenis] = useState<JenisOption | null>(null);
  const [selectedSifat, setSelectedSifat] = useState<SifatOption | null>(null);
  const [nomorSurat, setNomorSurat] = useState("");
  const [tanggalSurat, setTanggalSurat] = useState<Date | null>(null);

  // Pengirim manual
  const [pengirimNama, setPengirimNama] = useState("");
  const [pengirimJabatan, setPengirimJabatan] = useState("");
  const [pengirimInstansi, setPengirimInstansi] = useState("");
  const [pengirimEmail, setPengirimEmail] = useState("");

  const [selectedPerihal, setSelectedPerihal] = useState<PerihalOption | null>(null);
  const [selectedTujuan, setSelectedTujuan] = useState<PejabatOption[]>([]);

  const [perihal, setPerihal] = useState("");
  const [ringkasan, setRingkasan] = useState("");
  const [pemberkasan, setPemberkasan] = useState("");

  const [files, setFiles] = useState<FileList | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // Captcha state
  const [captcha, setCaptcha] = useState(generateCaptcha());
  const [inputCaptcha, setInputCaptcha] = useState("");


  const formSubmitHandle = async (e: FormEvent) => {
    e.preventDefault();

    // Validasi Captcha terlebih dahulu
    if (captcha !== inputCaptcha.toUpperCase()) {
      toast.error("Captcha tidak sesuai, mohon input kembali.");
      setCaptcha(generateCaptcha());
      setInputCaptcha("");
      return;
    }

    // Validasi form
    if (!nomorSurat || !selectedJenis || !selectedSifat) {
      toast.error("Mohon lengkapi Nomor Surat, Jenis & Sifat Surat.");
      return;
    }

    if (!tanggalSurat) {
      toast.error("Tanggal Surat wajib dipilih.");
      return;
    }

    if (!pengirimNama.trim()) {
      toast.error("Nama Pengirim wajib diisi.");
      return;
    }

    if (!pengirimJabatan.trim()) {
      toast.error("Jabatan Pengirim wajib diisi.");
      return;
    }

    if (!selectedPerihal) {
      toast.error("Perihal wajib dipilih.");
      return;
    }

    if (selectedTujuan.length === 0) {
      toast.error("Tujuan wajib dipilih minimal satu.");
      return;
    }

    setLoading(true);

    const tahun = tanggalSurat.getFullYear();
    const bulan = String(tanggalSurat.getMonth() + 1).padStart(2, "0");
    const hari = String(tanggalSurat.getDate()).padStart(2, "0");
    const tanggalStr = `${tahun}-${bulan}-${hari}`;

    const hal_id = selectedPerihal?.value ?? null;
    const kode_hal = selectedPerihal?.kode ?? null;
    const nama_hal = selectedPerihal?.nama ?? null;

    const surat_tujuan: SuratPenerima[] = selectedTujuan.map((tujuan) => ({
      penerima_id: tujuan.value,
      penerima_nama: tujuan.nama ?? "",
      penerima_jabatan: tujuan.jabatan ?? "",
      penerima_unit: tujuan.unit_jabatan ?? "",
      tipe: 1,
    }));

    const payload: SuratMasukPayload = {
      tahun,
      sifat_id: selectedSifat?.value ?? 0,
      jenis_id: selectedJenis?.value ?? 0,
      nomor: nomorSurat,
      tanggal: tanggalStr,
      hal_id,
      hal_keterangan: perihal || null,
      kode_hal: kode_hal || null,
      ringkasan: ringkasan || null,
      pemberkasan: pemberkasan || null,
      // Pengirim dari form manual dengan ID null (external)
      pengirim_id: 99,
      pengirim_nama: pengirimNama,
      pengirim_jabatan: pengirimJabatan,
      pengirim_unit: pengirimInstansi || null,
      nama_hal,
      tipe: 1,
      surat_tujuan,
      surat_tembusan: [],
    };

    // console.log("Payload final dikirim ke API:", payload);

    try {
      const res = await fetch("/api/doServices", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          method: "POST",
          endpoint: "office/surat",
          isForm: false,
          isImage: false,
          body: payload,
        }),
      });

      if (!res.ok) {
        toast.error("Gagal mengirim surat (HTTP error).");
        setLoading(false);
        return;
      }

      const result: any = await res.json();

      if (result.http_code !== 200 && result.http_code !== 201) {
        toast.error(result.message || "Gagal mengirim surat.");
        setLoading(false);
        return;
      }

      const suratId: number = result.data?.id || result.body?.data?.id || 0;

      if (suratId && files) {
        await uploadLampiranFiles(suratId, files);
      }

      toast.success("Surat berhasil dikirim. Terima kasih!");
      setSubmitted(true);

      // Reset form
      setSelectedJenis(null);
      setSelectedSifat(null);
      setNomorSurat("");
      setTanggalSurat(null);
      setPengirimNama("");
      setPengirimJabatan("");
      setPengirimInstansi("");
      setPengirimEmail("");
      setSelectedPerihal(null);
      setSelectedTujuan([]);
      setPerihal("");
      setRingkasan("");
      setPemberkasan("");
      setFiles(null);

    } catch (error) {
      console.error(error);
      toast.error("Terjadi kesalahan saat mengirim surat.");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitAnother = () => {
    setSubmitted(false);
  };

  return (
    <>
      <Head>
        <title>Kotak Masuk - Kirim Surat</title>
        <meta name="description" content="Formulir pengiriman surat masuk publik" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
      </Head>

      <div className="kotak-masuk-container">
        <div className="kotak-masuk-wrapper">
          {/* Header */}
          <div className="kotak-masuk-header">
            <div><CommonLogo alignLogo="text-center" /></div>
            <p className="header-subtitle">Kirim surat masuk Anda secara digital dengan mudah dan cepat</p>
          </div>

          {submitted ? (
            <div className="success-container">
              <div className="success-icon">
                <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                  <polyline points="22 4 12 14.01 9 11.01"></polyline>
                </svg>
              </div>
              <h2>Surat Berhasil Dikirim!</h2>
              <p>Terima kasih telah mengirim surat Anda. Surat akan segera diproses oleh pihak terkait.</p>
              <button
                className="btn-submit-another"
                onClick={handleSubmitAnother}
              >
                Kirim Surat Lainnya
              </button>
            </div>
          ) : (
            <form onSubmit={formSubmitHandle} className="kotak-masuk-form">
              {/* Section: Informasi Surat */}
              <div className="form-section">
                <h3 className="section-title">
                  <span className="section-number">1</span>
                  Informasi Surat
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Jenis Surat <span className="required">*</span></label>
                    <JenisSelect
                      value={selectedJenis}
                      onChange={setSelectedJenis}
                      placeholder="Pilih jenis surat..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Sifat Surat <span className="required">*</span></label>
                    <SifatSelect
                      value={selectedSifat}
                      onChange={setSelectedSifat}
                      placeholder="Pilih sifat surat..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Nomor Surat <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={nomorSurat}
                      onChange={(e) => setNomorSurat(e.target.value)}
                      placeholder="Masukkan nomor surat..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Tanggal Surat <span className="required">*</span></label>
                    <TanggalPicker
                      value={tanggalSurat}
                      onChange={setTanggalSurat}
                      placeholder="Pilih tanggal..."
                      className="form-input"
                    />
                  </div>
                </div>
              </div>

              {/* Section: Informasi Pengirim */}
              <div className="form-section">
                <h3 className="section-title">
                  <span className="section-number">2</span>
                  Informasi Pengirim
                </h3>
                <div className="form-grid">
                  <div className="form-group">
                    <label className="form-label">Nama Pengirim <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={pengirimNama}
                      onChange={(e) => setPengirimNama(e.target.value)}
                      placeholder="Masukkan nama pengirim..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Jabatan Pengirim <span className="required">*</span></label>
                    <input
                      type="text"
                      className="form-input"
                      value={pengirimJabatan}
                      onChange={(e) => setPengirimJabatan(e.target.value)}
                      placeholder="Masukkan jabatan pengirim..."
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Instansi / Lembaga</label>
                    <input
                      type="text"
                      className="form-input"
                      value={pengirimInstansi}
                      onChange={(e) => setPengirimInstansi(e.target.value)}
                      placeholder="Masukkan instansi/lembaga..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <input
                      type="email"
                      className="form-input"
                      value={pengirimEmail}
                      onChange={(e) => setPengirimEmail(e.target.value)}
                      placeholder="Masukkan email (opsional)..."
                    />
                  </div>
                </div>
              </div>

              {/* Section: Perihal & Tujuan */}
              <div className="form-section">
                <h3 className="section-title">
                  <span className="section-number">3</span>
                  Perihal & Tujuan
                </h3>
                <div className="form-grid">
                  <div className="form-group full-width">
                    <label className="form-label">Perihal <span className="required">*</span></label>
                    <PerihalSelect
                      value={selectedPerihal}
                      onChange={setSelectedPerihal}
                      placeholder="Pilih perihal..."
                    />
                  </div>

                  <div className="form-group full-width">
                    <label className="form-label">Tujuan <span className="required">*</span></label>
                    <PejabatMultiSelect
                      value={selectedTujuan}
                      onChange={(value) => setSelectedTujuan(Array.isArray(value) ? value : value ? [value] : [])}
                      placeholder="Ketik untuk mencari pegawai..."
                      isMulti={true}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Keterangan Hal</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={perihal}
                      onChange={(e) => setPerihal(e.target.value)}
                      placeholder="Masukkan keterangan perihal..."
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Isi / Ringkasan</label>
                    <textarea
                      className="form-textarea"
                      rows={3}
                      value={ringkasan}
                      onChange={(e) => setRingkasan(e.target.value)}
                      placeholder="Masukkan ringkasan isi surat..."
                    />
                  </div>
                </div>
              </div>

              {/* Section: Lampiran */}
              <div className="form-section">
                <h3 className="section-title">
                  <span className="section-number">4</span>
                  Lampiran
                </h3>
                <div className="form-group full-width">
                  <label className="form-label">Unggah Surat & Lampiran</label>
                  <div className="file-upload-area">
                    <input
                      type="file"
                      multiple
                      accept=".pdf,application/pdf"
                      className="file-input"
                      id="file-upload"
                      onChange={(e) => setFiles(e.target.files)}
                    />
                    <label htmlFor="file-upload" className="file-upload-label">
                      <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                        <polyline points="17 8 12 3 7 8"></polyline>
                        <line x1="12" y1="3" x2="12" y2="15"></line>
                      </svg>
                      <span>Klik untuk memilih file atau drag & drop</span>
                      <span className="file-hint">Hanya file PDF (Max 10MB)</span>
                    </label>
                  </div>
                  {files && files.length > 0 && (
                    <div className="file-list">
                      {Array.from(files).map((file, index) => (
                        <div key={index} className="file-item">
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                          </svg>
                          <span>{file.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Section: Verifikasi Captcha */}
              <div className="form-section">
                <h3 className="section-title">
                  <span className="section-number">5</span>
                  Verifikasi
                </h3>
                <div className="captcha-container">
                  <div className="captcha-display">
                    <span className="captcha-code">{captcha}</span>
                  </div>
                  <div className="form-group">
                    <input
                      type="text"
                      className="form-input captcha-input"
                      value={inputCaptcha}
                      onChange={(e) => setInputCaptcha(e.target.value.toUpperCase())}
                      placeholder=""
                      maxLength={4}
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Submit Button */}
              <div className="form-actions">
                <button
                  type="submit"
                  className="btn-submit"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Mengirim...
                    </>
                  ) : (
                    <>
                      <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"></line>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                      </svg>
                      Kirim Surat
                    </>
                  )}
                </button>
              </div>
            </form>
          )}

          {/* Footer */}
          <div className="kotak-masuk-footer">
            <p>© {new Date().getFullYear()} Office Digital Mail System. All rights reserved.</p>
          </div>
        </div>
      </div>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="colored"
      />

      <style jsx>{`
        .kotak-masuk-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
          padding: 40px 20px;
          font-family: 'Inter', 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
        }

        .kotak-masuk-wrapper {
          max-width: 900px;
          margin: 0 auto;
        }

        .kotak-masuk-header {
          text-align: center;
          margin-bottom: 40px;
          color: white;
        }

        .header-logo {
          display: inline-block;
          margin-bottom: 24px;
          animation: float 3s ease-in-out infinite;
        }

        .ido-logo {
          width: auto;
          height: 100px;
          max-width: 280px;
          object-fit: contain;
          filter: drop-shadow(0 10px 30px rgba(255, 255, 255, 0.2));
        }

        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }

        .kotak-masuk-header h1 {
          font-size: 2.5rem;
          font-weight: 700;
          margin: 0 0 12px;
          background: linear-gradient(to right, #fff, #e0e7ff);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-subtitle {
          font-size: 1.1rem;
          color: rgba(255, 255, 255, 0.7);
          margin: 0;
        }

        .kotak-masuk-form {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 0;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
          overflow: hidden;
        }

        .form-section {
          padding: 32px 40px;
          border-bottom: 1px solid rgba(0, 0, 0, 0.08);
        }

        .form-section:last-of-type {
          border-bottom: none;
        }

        .section-title {
          display: flex;
          align-items: center;
          gap: 12px;
          font-size: 1.25rem;
          font-weight: 600;
          color: #1a1a2e;
          margin: 0 0 24px;
        }

        .section-number {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 50%;
          font-size: 0.875rem;
          font-weight: 600;
        }

        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          grid-column: 1 / -1;
        }

        .form-label {
          font-size: 0.875rem;
          font-weight: 500;
          color: #374151;
          margin-bottom: 8px;
        }

        .required {
          color: #ef4444;
        }

        .form-input,
        .form-select,
        .form-textarea {
          width: 100%;
          padding: 12px 16px;
          font-size: 0.95rem;
          border: 2px solid #e5e7eb;
          border-radius: 12px;
          outline: none;
          transition: all 0.3s ease;
          background: white;
        }

        .form-input:focus,
        .form-select:focus,
        .form-textarea:focus {
          border-color: #667eea;
          box-shadow: 0 0 0 4px rgba(102, 126, 234, 0.1);
        }

        .form-input::placeholder,
        .form-textarea::placeholder {
          color: #9ca3af;
        }

        .form-textarea {
          resize: vertical;
          min-height: 100px;
        }

        .selected-items {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          margin-top: 12px;
        }

        .selected-item {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 12px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 8px;
          font-size: 0.875rem;
        }

        .remove-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 20px;
          height: 20px;
          background: rgba(255, 255, 255, 0.2);
          border: none;
          border-radius: 50%;
          color: white;
          font-size: 1rem;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .remove-btn:hover {
          background: rgba(255, 255, 255, 0.3);
        }

        .file-upload-area {
          position: relative;
        }

        .file-input {
          position: absolute;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }

        .file-upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 40px;
          border: 2px dashed #d1d5db;
          border-radius: 12px;
          background: #f9fafb;
          cursor: pointer;
          transition: all 0.3s ease;
          text-align: center;
          color: #6b7280;
        }

        .file-upload-label:hover {
          border-color: #667eea;
          background: rgba(102, 126, 234, 0.05);
        }

        .file-upload-label svg {
          margin-bottom: 12px;
          color: #667eea;
        }

        .file-hint {
          font-size: 0.75rem;
          color: #9ca3af;
          margin-top: 4px;
        }

        .file-list {
          margin-top: 16px;
          display: flex;
          flex-direction: column;
          gap: 8px;
        }

        .file-item {
          display: flex;
          align-items: center;
          gap: 8px;
          padding: 12px 16px;
          background: #f3f4f6;
          border-radius: 8px;
          font-size: 0.875rem;
          color: #374151;
        }

        .file-item svg {
          color: #667eea;
        }

        /* Captcha Styles */
        .captcha-container {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 20px;
          padding: 20px;
        }

        .captcha-display {
          display: flex;
          align-items: center;
          gap: 16px;
        }

        .captcha-code {
          font-size: 2rem;
          font-weight: 700;
          letter-spacing: 8px;
          padding: 16px 32px;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          border-radius: 12px;
          font-family: 'Courier New', monospace;
          text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.2);
          user-select: none;
        }

        .captcha-refresh {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 44px;
          height: 44px;
          background: #f3f4f6;
          border: 2px solid #e5e7eb;
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
          color: #6b7280;
        }

        .captcha-refresh:hover {
          background: #667eea;
          border-color: #667eea;
          color: white;
          transform: rotate(180deg);
        }

        .captcha-input {
          text-align: center;
          font-size: 1.2rem;
          font-weight: 600;
          letter-spacing: 6px;
          max-width: 200px;
          text-transform: uppercase;
        }

        .form-actions {
          padding: 24px 40px 32px;
          text-align: center;
        }

        .btn-submit {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          padding: 16px 48px;
          font-size: 1rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }

        .btn-submit:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(102, 126, 234, 0.5);
        }

        .btn-submit:disabled {
          opacity: 0.7;
          cursor: not-allowed;
        }

        .loading-spinner {
          width: 20px;
          height: 20px;
          border: 2px solid rgba(255, 255, 255, 0.3);
          border-top-color: white;
          border-radius: 50%;
          animation: spin 0.8s linear infinite;
        }

        @keyframes spin {
          to { transform: rotate(360deg); }
        }

        .success-container {
          background: rgba(255, 255, 255, 0.95);
          backdrop-filter: blur(20px);
          border-radius: 24px;
          padding: 60px 40px;
          text-align: center;
          box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
        }

        .success-icon {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          width: 120px;
          height: 120px;
          background: linear-gradient(135deg, #10b981 0%, #059669 100%);
          border-radius: 50%;
          margin-bottom: 24px;
          color: white;
          animation: scaleIn 0.5s ease;
        }

        @keyframes scaleIn {
          0% { transform: scale(0); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }

        .success-container h2 {
          font-size: 1.75rem;
          font-weight: 700;
          color: #1a1a2e;
          margin: 0 0 12px;
        }

        .success-container p {
          font-size: 1rem;
          color: #6b7280;
          margin: 0 0 32px;
        }

        .btn-submit-another {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 14px 32px;
          font-size: 1rem;
          font-weight: 600;
          color: white;
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
          box-shadow: 0 10px 30px rgba(102, 126, 234, 0.4);
        }

        .btn-submit-another:hover {
          transform: translateY(-2px);
          box-shadow: 0 15px 40px rgba(102, 126, 234, 0.5);
        }

        .kotak-masuk-footer {
          text-align: center;
          margin-top: 32px;
          color: rgba(255, 255, 255, 0.5);
          font-size: 0.875rem;
        }

        @media (max-width: 768px) {
          .kotak-masuk-container {
            padding: 20px 16px;
          }

          .kotak-masuk-header h1 {
            font-size: 1.75rem;
          }

          .form-section {
            padding: 24px 20px;
          }

          .form-grid {
            grid-template-columns: 1fr;
          }

          .form-actions {
            padding: 20px;
          }

          .btn-submit {
            width: 100%;
            padding: 14px 24px;
          }
        }

        :global(.datepicker) {
          width: 100%;
        }


      `}</style>
    </>
  );
};

export default KotakMasukPage;
